import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Badge, Grid, Group, Skeleton, Stack, Text } from "@mantine/core";
import { Alert, Button, ConfirmModal, PageHeader, SectionCard } from "@xplatform/design-system";
import { IconKey, IconPlus, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { ApiKey, LicenceInstance } from "@models/access";
import { accessApi } from "@services/accessApi";
import { AdminTable, col } from "@components/AdminTable";
import { useAccessScope, useLicenceInstances, useLicences, useOrganisationTree } from "@hooks/access";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import { EnvironmentBanner, NameModal, OrganisationTree, organisationName } from "@features/Access";
import { errorMessage } from "@hooks/monitoring/useApiResource";

/**
 * API keys of this application: pick an organisation, see its licence instances
 * (instance-based access) and their keys, create an instance or a key, revoke a
 * key. A key carries the permissions of its organisation's licence. BeeHive mails
 * a new key to the admin who creates it; this page never sees the secret.
 */
export const ApiKeysPage = () => {
  const { t } = useTranslation();
  const { canManageAccess } = useAdminPermissions();
  const [params, setParams] = useSearchParams();
  const scope = useAccessScope();
  const tree = useOrganisationTree();
  const roots = useMemo(() => tree.data ?? [], [tree.data]);
  const organisationId = params.get("organisationId") ?? scope.data?.rootOrganisationId ?? roots[0]?.id ?? null;
  const instances = useLicenceInstances(organisationId);
  const licences = useLicences(canManageAccess);
  const [creatingInstance, setCreatingInstance] = useState(false);
  const [keyFor, setKeyFor] = useState<LicenceInstance | null>(null);
  const [revoking, setRevoking] = useState<{ instance: LicenceInstance; key: ApiKey } | null>(null);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const select = (id: string) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.set("organisationId", id);
      return next;
    }, { replace: true });

  const orgName = organisationId ? organisationName(roots, organisationId) ?? organisationId : "";
  const licenceOptions = useMemo(
    () => (licences.data ?? []).map((l) => ({ value: l.id, label: l.model ? `${l.name} (${l.model})` : l.name })),
    [licences.data],
  );

  const keyColumns = (instance: LicenceInstance) => [
    col.text<ApiKey>("name", t("admin.access.apiKeys.columns.name"), (k) => k.name),
    col.status<ApiKey>("status", t("admin.access.apiKeys.columns.status"), (k) => k.status),
    col.date<ApiKey>("lastUsed", t("admin.access.apiKeys.columns.lastUsed"), (k) => k.lastUsed),
    col.actions<ApiKey>("", (k) => [{
      label: t("admin.access.apiKeys.revoke"),
      icon: <IconTrash size={16} />,
      color: "red",
      hidden: !canManageAccess || k.status === "Revoked",
      onClick: () => setRevoking({ instance, key: k }),
    }]),
  ];

  return (
    <Stack gap="md">
      <PageHeader
        title={t("admin.access.apiKeys.title")}
        description={t("admin.access.apiKeys.description")}
        icon={<IconKey size={28} />}
        actions={canManageAccess && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => setCreatingInstance(true)}
                  disabled={!organisationId || !licences.data}>
            {t("admin.access.apiKeys.newInstance")}
          </Button>
        )}
      />
      <EnvironmentBanner />

      <Grid gutter="md">
        <Grid.Col span={{ base: 12, md: 4, lg: 3 }}>
          <SectionCard title={t("admin.access.users.organisations")}>
            {tree.error && <Alert color="red">{tree.error}</Alert>}
            {!tree.data && !tree.error ? <Skeleton h={120} /> : (
              <OrganisationTree roots={roots} selectedId={organisationId} onSelect={select} />
            )}
          </SectionCard>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 8, lg: 9 }}>
          <Stack gap="md">
            <Text size="sm" c="dimmed">{t("admin.access.apiKeys.mailed")}</Text>
            {notice && <Alert color="green" withCloseButton onClose={() => setNotice(null)}>{notice}</Alert>}
            {error && <Alert color="red" withCloseButton onClose={() => setError(null)}>{error}</Alert>}
            {instances.error && <Alert color={instances.forbidden ? "gray" : "red"}>{instances.error}</Alert>}
            {instances.loading && !instances.data && <Skeleton h={160} />}
            {instances.data && instances.data.length === 0 && (
              <SectionCard title={t("admin.access.apiKeys.instancesOf", { organisation: orgName })}>
                <Text size="sm" c="dimmed">{t("admin.access.apiKeys.noInstances")}</Text>
              </SectionCard>
            )}
            {(instances.data ?? []).map((instance) => (
              <SectionCard
                key={instance.id}
                title={
                  <Group gap="xs">
                    <Text fw={600}>{instance.name ?? instance.id}</Text>
                    {instance.licenceName && <Badge variant="light" tt="none">{instance.licenceName}</Badge>}
                    {instance.status && <Badge variant="outline" color="gray">{instance.status}</Badge>}
                  </Group>
                }
                actions={canManageAccess && (
                  <Button size="xs" variant="light" leftSection={<IconPlus size={14} />}
                          onClick={() => setKeyFor(instance)}>
                    {t("admin.access.apiKeys.newKey")}
                  </Button>
                )}
              >
                <AdminTable
                  columns={keyColumns(instance)}
                  data={instance.keys}
                  getRowKey={(k) => k.id}
                  emptyMessage={t("admin.access.apiKeys.noKeys")}
                />
              </SectionCard>
            ))}
          </Stack>
        </Grid.Col>
      </Grid>

      <NameModal
        opened={creatingInstance}
        title={t("admin.access.apiKeys.newInstanceTitle", { organisation: orgName })}
        hint={t("admin.access.apiKeys.newInstanceHint")}
        nameLabel={t("admin.access.apiKeys.instanceName")}
        submitLabel={t("admin.access.apiKeys.newInstance")}
        choice={{ label: t("admin.access.apiKeys.licence"), options: licenceOptions }}
        onClose={() => setCreatingInstance(false)}
        onSubmit={async (name, licenceId) => {
          await accessApi().createInstance(organisationId!, { name, licenceId: licenceId! });
          instances.reload();
        }}
      />
      <NameModal
        opened={keyFor !== null}
        title={t("admin.access.apiKeys.newKeyTitle", { instance: keyFor?.name ?? "" })}
        hint={t("admin.access.apiKeys.newKeyHint")}
        nameLabel={t("admin.access.apiKeys.keyName")}
        submitLabel={t("admin.access.apiKeys.newKey")}
        onClose={() => setKeyFor(null)}
        onSubmit={async (name) => {
          const key = await accessApi().createApiKey(keyFor!.id, { name });
          setNotice(t("admin.access.apiKeys.created", { name: key.name, email: key.recipientEmail ?? "" }));
          instances.reload();
        }}
      />
      <ConfirmModal
        opened={revoking !== null}
        onClose={() => setRevoking(null)}
        onConfirm={async () => {
          if (!revoking) return;
          setRevokeBusy(true);
          try {
            await accessApi().revokeApiKey(revoking.instance.id, revoking.key.id);
            instances.reload();
          } catch (err) {
            setError(errorMessage(err));
          } finally {
            setRevokeBusy(false);
            setRevoking(null);
          }
        }}
        title={t("admin.access.apiKeys.revokeTitle")}
        message={t("admin.access.apiKeys.revokeMessage", { name: revoking?.key.name ?? "" })}
        confirmLabel={t("admin.access.apiKeys.revoke")}
        cancelLabel={t("common.cancel")}
        loading={revokeBusy}
        danger
        portalProps={{ target: "#admin-portal-root" }}
      />
    </Stack>
  );
};
